'use client';

import { FC } from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Grid } from '@purpur/library';
import { match } from 'ts-pattern';

import { Carousel } from '@telialt/common-carousel/src';

import { BlockPromoType } from '../../models/Content';
import { getBlockId } from '../../utils/content';
import { BlockHeader } from '../common/BlockHeader';
import { BlockPromoCardsContainer, BlockPromoMobileCarouselContainer, BlockPromoSection } from './BlockPromo.styles';
import { BlockPromoDisplayVariant } from './model';
import { PromoCard } from './PromoCard';
import { transformBlockPromo } from './transformers';

type Props = {
    contentItem: BlockPromoType;
};

const MOBILE_CAROUSEL_DISPLAY_VARIANTS: BlockPromoDisplayVariant[] = ['1-1-1', '1-2', '1-3', '2-2'];

export const BlockPromo: FC<Props> = ({ contentItem: initialContentItem }) => {
    const contentItem = useContentfulLiveUpdates(initialContentItem);

    const { backgroundColor, displayVariant, blockTitle, blockCta, isHeaderNegative, cards } =
        transformBlockPromo(contentItem);

    const cardsLimit = match<BlockPromoDisplayVariant, number>(displayVariant)
        .with('1-1-portrait', () => 2)
        .with('1-1-landscape', () => 2)
        .with('1-1-1', () => 3)
        .with('1-2', () => 3)
        .with('2-2', () => 4)
        .with('1-3', () => 4)
        .otherwise(() => cards?.length || 0);

    const hasMobileCarousel = MOBILE_CAROUSEL_DISPLAY_VARIANTS.includes(displayVariant);

    const cardSlides = cards
        ?.slice(0, cardsLimit)
        .map((card, index) => (
            <PromoCard key={card.id} card={card} titleTag={blockTitle ? 'h3' : 'h2'} itemIndex={index} />
        ));

    return (
        <BlockPromoSection
            $backgroundVariant={backgroundColor}
            data-section="Promo Card Block"
            data-section-name={blockTitle}
            data-section-display-option={displayVariant}
            data-section-color-option={backgroundColor}
            id={getBlockId(contentItem)}
        >
            <Grid noGap={true}>
                <BlockHeader title={blockTitle} cta={blockCta} isNegative={isHeaderNegative} />
                <BlockPromoCardsContainer $displayVariant={displayVariant}>
                    {cards?.slice(0, cardsLimit).map((card, index) => (
                        <li key={card.id}>
                            <PromoCard card={card} titleTag={blockTitle ? 'h3' : 'h2'} itemIndex={index} />
                        </li>
                    ))}
                </BlockPromoCardsContainer>
                {hasMobileCarousel && (
                    <BlockPromoMobileCarouselContainer>
                        <Carousel variant="dynamic-width" slides={cardSlides} />
                    </BlockPromoMobileCarouselContainer>
                )}
            </Grid>
        </BlockPromoSection>
    );
};
